import { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import moment from "moment";
import { useAppSelector } from "store/hooks";
import { userMetadataSelector } from "store/selectors";

import {
  AiAssistantChatHistoryItem,
  AiAssistantChatMessageAuthor,
  AiAssistantJSONChatMessageType,
  AiAssistantSubmissionChatMessage,
  PayrollSubmissionResponse,
} from "types/aiAssistant";
import { defaultTimeFormat } from "constants/shared/locale";
import { isAiAssistantSubmissionChatMessage } from "helpers/aiAssistant";
import { isPayrollSubmissionResponse } from "helpers/aiAssistant/payrollRun";

import { Button, ListSectionItemContainer, Text } from "uikit";
import ButtonsContainer from "uikit/Containers/ButtonsContainer";

import { MarkdownMessage } from "./MarkdownMessage";
import { PayrollSubmissionSummary } from "./PayrollSubmissionSummary";
import {
  ACICContent,
  ACICHeader,
  AiChatItemContainer,
  PayrollSubmissionSummaryWarningSection,
} from "./styles";

interface IProps {
  data: AiAssistantChatHistoryItem;
  onSubmitButtonClick: (actionType: AiAssistantJSONChatMessageType) => void;
  onCancelButtonClick: () => void;
  isButtonDisabled: boolean;
  isLoadingAfterSubmit: boolean;
}

export const AiChatItem: FC<IProps> = ({
  data,
  onSubmitButtonClick,
  onCancelButtonClick,
  isButtonDisabled,
  isLoadingAfterSubmit,
}) => {
  const currentUser = useAppSelector(userMetadataSelector);
  const { t } = useTranslation();
  const translationPrefix = `ai_assistant_pages.shared`;
  const shouldShowButtons = isAiAssistantSubmissionChatMessage(data.message);

  const renderDate = (timeStamp: string) => {
    const today = moment().startOf("day");
    const targetDate = moment(timeStamp).startOf("day");
    const todayText = t("common.today");

    if (today.isSame(targetDate)) {
      return `${todayText} ${moment(timeStamp).format(defaultTimeFormat)}`;
    }

    return moment(timeStamp).format(`dddd ${defaultTimeFormat}`);
  };
  const renderAuthorName = (author: AiAssistantChatMessageAuthor) => {
    if (author === AiAssistantChatMessageAuthor.ASSISTANT) {
      return t(`${translationPrefix}.ai_assistant_author_name`);
    }
    return currentUser?.personalInfo?.firstName;
  };

  const renderPayrollSubmissionMessage = (payrollData: PayrollSubmissionResponse) => {
    const shouldShowWarnings =
      !!payrollData?.payroll.warnings.length ||
      !!payrollData?.payroll.workerWarnings ||
      !!payrollData?.payroll.workerBlockers;
    return (
      <>
        <Text $fs="t14">{payrollData?.question}</Text>
        {shouldShowWarnings && (
          <>
            <Text $fs="t14" $fw="semiBold" $mt="10px">
              <Trans i18nKey={`ai_assistant_pages.admin.payroll_run.warnings.title`} />
            </Text>
            <PayrollSubmissionSummaryWarningSection hideListDecoration={false}>
              {payrollData?.payroll.warnings.length > 0 &&
                payrollData?.payroll.warnings.map((warning, index) => (
                  <ListSectionItemContainer key={index.toString()}>
                    <Text $fs="t12" $fw="regular" $variant="secondary">
                      {warning.message}
                    </Text>
                  </ListSectionItemContainer>
                ))}
              {payrollData?.payroll.workerWarnings && (
                <ListSectionItemContainer key="worker-warnings">
                  <Text $fs="t12" $fw="regular" $variant="secondary">
                    {payrollData?.payroll.workerWarnings}
                  </Text>
                </ListSectionItemContainer>
              )}
              {payrollData?.payroll.workerBlockers && (
                <ListSectionItemContainer key="worker-blockers">
                  <Text $fs="t12" $fw="regular" $variant="secondary">
                    {payrollData?.payroll.workerBlockers}
                  </Text>
                </ListSectionItemContainer>
              )}
            </PayrollSubmissionSummaryWarningSection>
          </>
        )}
      </>
    );
  };

  const renderMessage = () => {
    if (data.author === AiAssistantChatMessageAuthor.USER) {
      return <Text $fs="t14">{data.message as string}</Text>;
    }

    if (!isAiAssistantSubmissionChatMessage(data.message)) {
      return <MarkdownMessage content={data.message.trim()} />;
    }

    if (isPayrollSubmissionResponse(data.message)) {
      return renderPayrollSubmissionMessage(data.message);
    }

    return <MarkdownMessage content={data?.message?.question?.trim()} />;
  };

  const renderPayrollSubmissionSummary = () => {
    if (
      isAiAssistantSubmissionChatMessage(data.message) &&
      isPayrollSubmissionResponse(data.message)
    ) {
      const payrollData = data.message;
      return <PayrollSubmissionSummary data={payrollData} />;
    }
  };

  return (
    <AiChatItemContainer $author={data.author}>
      <ACICHeader $author={data.author}>
        <Text className="author-name" $fs="t12" $fw="regular" $variant="secondary">
          {renderAuthorName(data.author)}
        </Text>

        <Text $fs="t12" $fw="regular" $variant="secondary">
          {renderDate(data.timestamp)}
        </Text>
      </ACICHeader>
      <ACICContent $author={data.author}>{renderMessage()}</ACICContent>

      {renderPayrollSubmissionSummary()}

      {shouldShowButtons && (
        <ButtonsContainer $justify="space-between" $mt="8px">
          <Button
            onClick={() =>
              onSubmitButtonClick((data?.message as AiAssistantSubmissionChatMessage)?.type)
            }
            disabled={isButtonDisabled || isLoadingAfterSubmit}
            data-testid="submit-payroll-btn"
            type={"submit"}
          >
            <Trans i18nKey={`buttons.submit`} />
          </Button>

          <Button
            onClick={onCancelButtonClick}
            disabled={isButtonDisabled || isLoadingAfterSubmit}
            data-testid="cancel-submit-payroll-btn"
            $variant="ghost"
          >
            <Trans i18nKey={`buttons.cancel`} />
          </Button>
        </ButtonsContainer>
      )}
    </AiChatItemContainer>
  );
};
